import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  Mail, 
  RefreshCw, 
  CheckCircle, 
  XCircle, 
  Clock, 
  AlertTriangle,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Eye,
  RotateCcw
} from 'lucide-react';
import api from '../../services/api';

const EmailLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    status: '',
    email_type: '',
    recipient: ''
  });
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  const statusColors = {
    pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', icon: Clock },
    accepted: { bg: 'bg-blue-500/20', text: 'text-blue-400', icon: Clock },
    delivered: { bg: 'bg-green-500/20', text: 'text-green-400', icon: CheckCircle },
    failed: { bg: 'bg-red-500/20', text: 'text-red-400', icon: XCircle },
    forbidden: { bg: 'bg-red-500/20', text: 'text-red-400', icon: XCircle },
    auth_error: { bg: 'bg-red-500/20', text: 'text-red-400', icon: XCircle },
    config_error: { bg: 'bg-red-500/20', text: 'text-red-400', icon: XCircle },
    domain_not_found: { bg: 'bg-red-500/20', text: 'text-red-400', icon: XCircle },
    bad_request: { bg: 'bg-red-500/20', text: 'text-red-400', icon: XCircle },
    rate_limited: { bg: 'bg-orange-500/20', text: 'text-orange-400', icon: AlertTriangle },
    timeout: { bg: 'bg-orange-500/20', text: 'text-orange-400', icon: AlertTriangle },
    connection_error: { bg: 'bg-orange-500/20', text: 'text-orange-400', icon: AlertTriangle },
    bounced: { bg: 'bg-orange-500/20', text: 'text-orange-400', icon: AlertTriangle },
    complained: { bg: 'bg-purple-500/20', text: 'text-purple-400', icon: AlertTriangle },
    opened: { bg: 'bg-cyan-500/20', text: 'text-cyan-400', icon: Eye },
  };

  const emailTypes = {
    password_reset: 'Réinitialisation MDP',
    verification: 'Vérification email',
    payment_confirmation: 'Confirmation paiement',
    credentials: 'Identifiants IPTV',
    credentials_ready: 'Identifiants prêts',
    access_pending: 'Accès en attente',
    payment_link: 'Lien de paiement',
    referral_payment_link: 'Lien parrainage',
    ticket_created: 'Ticket créé',
    ticket_reply: 'Réponse ticket',
    ticket_status_changed: 'Statut ticket',
    vendor_pending: 'Vendeur en attente',
    vendor_approved: 'Vendeur approuvé',
    subscription_reminder: 'Rappel abonnement',
    general: 'Général'
  };

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: page.toString(), per_page: '20' });
      if (filters.status) params.append('status', filters.status);
      if (filters.email_type) params.append('email_type', filters.email_type);
      if (filters.recipient) params.append('recipient', filters.recipient);

      const response = await api.get(`/admin/email-logs?${params}`);
      setLogs(response.data.logs);
      setTotalPages(response.data.total_pages);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching email logs:', error);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/email-logs/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching email stats:', error);
    }
  };

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [fetchLogs]);

  const handleResend = async (logId) => {
    try {
      await api.post(`/admin/email-logs/resend/${logId}`);
      fetchLogs();
    } catch (error) {
      console.error('Error resending email:', error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const StatusBadge = ({ status }) => {
    const config = statusColors[status] || statusColors.pending;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon className="w-3 h-3" />
        {status}
      </span>
    );
  };

  return (
    <div className="min-h-screen py-8 px-4" data-testid="email-logs-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <Mail className="w-8 h-8 text-[#FF6B35]" />
            Registre des Emails
          </h1>
          <p className="text-[#B8B8BA]">Suivez tous les emails envoyés et leur statut de délivrance</p>
        </motion.div>

        {/* Stats Cards */}
        {stats && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8"
          >
            <div className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-xl p-4">
              <div className="text-sm text-[#B8B8BA] mb-1">Total Emails</div>
              <div className="text-2xl font-bold text-white">{stats.total_emails}</div>
            </div>
            <div className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-xl p-4">
              <div className="text-sm text-[#B8B8BA] mb-1">Dernières 24h</div>
              <div className="text-2xl font-bold text-white">{stats.last_24h?.total || 0}</div>
            </div>
            <div className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-xl p-4">
              <div className="text-sm text-[#B8B8BA] mb-1">Taux de délivrance</div>
              <div className="text-2xl font-bold text-green-400">{stats.last_24h?.delivery_rate || 0}%</div>
            </div>
            <div className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-xl p-4">
              <div className="text-sm text-[#B8B8BA] mb-1">Échecs (24h)</div>
              <div className="text-2xl font-bold text-red-400">{stats.last_24h?.failed || 0}</div>
            </div>
          </motion.div>
        )}

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-xl p-4 mb-6"
        >
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 text-[#B8B8BA]">
              <Filter className="w-4 h-4" />
              <span>Filtres:</span>
            </div>
            
            {/* Status filter */}
            <select
              value={filters.status}
              onChange={(e) => { setFilters(f => ({ ...f, status: e.target.value })); setPage(1); }}
              className="bg-[#222226] border border-[#3A3A3D] rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#FF6B35]"
            >
              <option value="">Tous les statuts</option>
              <option value="pending">En attente</option>
              <option value="accepted">Accepté</option>
              <option value="delivered">Délivré</option>
              <option value="failed">Échoué</option>
              <option value="bounced">Rebondi</option>
            </select>

            {/* Type filter */}
            <select
              value={filters.email_type}
              onChange={(e) => { setFilters(f => ({ ...f, email_type: e.target.value })); setPage(1); }}
              className="bg-[#222226] border border-[#3A3A3D] rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#FF6B35]"
            >
              <option value="">Tous les types</option>
              {Object.entries(emailTypes).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>

            {/* Recipient search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6B6E]" />
              <input
                type="text"
                placeholder="Rechercher destinataire..."
                value={filters.recipient}
                onChange={(e) => { setFilters(f => ({ ...f, recipient: e.target.value })); setPage(1); }}
                className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg pl-10 pr-4 py-2 text-white text-sm focus:outline-none focus:border-[#FF6B35]"
              />
            </div>

            {/* Refresh button */}
            <button
              onClick={() => { fetchLogs(); fetchStats(); }}
              className="p-2 bg-[#FF6B35] text-white rounded-lg hover:bg-[#FF6B35]/80 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </motion.div>

        {/* Logs Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-xl overflow-hidden"
        >
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 text-[#FF6B35] animate-spin" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-[#B8B8BA]">
              <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aucun email trouvé</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#3A3A3D] bg-[#222226]">
                    <th className="text-left px-4 py-3 text-sm font-medium text-[#B8B8BA]">Destinataire</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-[#B8B8BA]">Type</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-[#B8B8BA]">Sujet</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-[#B8B8BA]">Statut</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-[#B8B8BA]">Date</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-[#B8B8BA]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, index) => (
                    <tr
                      key={log.id}
                      className="border-b border-[#3A3A3D]/50 hover:bg-[#222226]/50 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <span className="text-white text-sm">{log.to_email}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-[#B8B8BA] text-sm">
                          {emailTypes[log.email_type] || log.email_type}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-[#B8B8BA] text-sm truncate max-w-[200px] block">
                          {log.subject}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={log.status} />
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-[#6B6B6E] text-sm">{formatDate(log.sent_at)}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => { setSelectedLog(log); setShowDetail(true); }}
                            className="p-1.5 text-[#B8B8BA] hover:text-white hover:bg-[#3A3A3D] rounded transition-colors"
                            title="Voir détails"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {['failed', 'bounced', 'rejected'].includes(log.status) && (
                            <button
                              onClick={() => handleResend(log.email_log_id)}
                              className="p-1.5 text-[#FF6B35] hover:text-white hover:bg-[#FF6B35] rounded transition-colors"
                              title="Renvoyer"
                            >
                              <RotateCcw className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-[#3A3A3D]">
              <span className="text-sm text-[#B8B8BA]">
                Page {page} sur {totalPages} ({total} emails)
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 text-white bg-[#3A3A3D] rounded-lg disabled:opacity-50 hover:bg-[#4A4A4D] transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-2 text-white bg-[#3A3A3D] rounded-lg disabled:opacity-50 hover:bg-[#4A4A4D] transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </motion.div>

        {/* Detail Modal */}
        {showDetail && selectedLog && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-white">Détails de l'email</h3>
                  <button
                    onClick={() => setShowDetail(false)}
                    className="text-[#B8B8BA] hover:text-white"
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-[#6B6B6E]">Destinataire</label>
                      <p className="text-white">{selectedLog.to_email}</p>
                    </div>
                    <div>
                      <label className="text-sm text-[#6B6B6E]">Statut</label>
                      <div className="mt-1">
                        <StatusBadge status={selectedLog.status} />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm text-[#6B6B6E]">Sujet</label>
                    <p className="text-white">{selectedLog.subject}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-[#6B6B6E]">Type</label>
                      <p className="text-white">{emailTypes[selectedLog.email_type] || selectedLog.email_type}</p>
                    </div>
                    <div>
                      <label className="text-sm text-[#6B6B6E]">Provider</label>
                      <p className="text-white capitalize">{selectedLog.provider}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-[#6B6B6E]">Date d'envoi</label>
                      <p className="text-white">{formatDate(selectedLog.sent_at)}</p>
                    </div>
                    <div>
                      <label className="text-sm text-[#6B6B6E]">Date de délivrance</label>
                      <p className="text-white">{formatDate(selectedLog.delivered_at)}</p>
                    </div>
                  </div>

                  {selectedLog.message_id && (
                    <div>
                      <label className="text-sm text-[#6B6B6E]">Message ID</label>
                      <p className="text-white font-mono text-sm break-all">{selectedLog.message_id}</p>
                    </div>
                  )}

                  {selectedLog.error && (
                    <div>
                      <label className="text-sm text-[#6B6B6E]">Erreur</label>
                      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mt-1">
                        <p className="text-red-400 text-sm font-mono break-all">{selectedLog.error}</p>
                      </div>
                    </div>
                  )}

                  {selectedLog.events && selectedLog.events.length > 0 && (
                    <div>
                      <label className="text-sm text-[#6B6B6E]">Événements</label>
                      <div className="mt-2 space-y-2">
                        {selectedLog.events.map((event, i) => (
                          <div key={i} className="bg-[#222226] rounded-lg p-3">
                            <div className="flex items-center justify-between">
                              <span className="text-white font-medium">{event.type}</span>
                              <span className="text-[#6B6B6E] text-sm">{formatDate(event.timestamp)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  {['failed', 'bounced', 'rejected'].includes(selectedLog.status) && (
                    <button
                      onClick={() => { handleResend(selectedLog.email_log_id); setShowDetail(false); }}
                      className="px-4 py-2 bg-[#FF6B35] text-white rounded-lg hover:bg-[#FF6B35]/80 flex items-center gap-2"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Renvoyer
                    </button>
                  )}
                  <button
                    onClick={() => setShowDetail(false)}
                    className="px-4 py-2 bg-[#3A3A3D] text-white rounded-lg hover:bg-[#4A4A4D]"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailLogsPage;
